// frontend/src/components/concierge/tabs/DetailsTab.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { CONCIERGE_CLASSIFICATIONS, daysUntil } from '../../../lib/conciergeTabs';
import { daysAgoText, fmtMonDay } from '../../../lib/conciergeActivityModel';
import { formatPhone } from '../../../lib/smsThreadModel';
import ProfileCard from './ProfileCard';
import PaymentPanel from '../PaymentPanel';

const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function CampRow({ c, pill }) {
  const [, m, d] = (c.start_date || '').split('-').map(Number);
  return (
    <div className="cn-row cn-camp">
      <div className="cn-camp__date"><span className="cn-camp__mon">{m ? MON[m - 1] : ''}</span><span className="cn-camp__day">{d || ''}</span></div>
      <div className="cn-camp__main"><div className="cn-camp__name">{c.name}</div><div className="cn-camp__sub">{c.location}</div></div>
      {pill}
    </div>
  );
}

export default function DetailsTab(props) {
  const { userId, me, owner, eligibleOwners, classification, onClassification, onOwner, contractStart, contractEnd,
    setContractStart, setContractEnd, onContractBlur, onEditProfile, profile, lastLoginAt, lastProfileChangeAt,
    lastManagedAt, todayIso } = props;
  const { API_URL } = useAuth();
  const [camps, setCamps] = useState(null);
  const [campsErr, setCampsErr] = useState('');

  useEffect(() => {
    const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    axios.get(`${API_URL}/concierge/${userId}/camps`, cfg)
      .then(res => setCamps({ registered: res.data?.registered || [], attended: res.data?.attended || [] }))
      .catch(() => { setCamps(null); setCampsErr('Failed to load camps.'); });
  }, [userId, API_URL]);

  const endsIn = contractEnd ? daysUntil(contractEnd, todayIso) : null;
  const endingSoon = endsIn !== null && endsIn >= 0 && endsIn <= 30;
  const u = profile?.user || {};
  const contacts = [
    { name: `${u.first_name || ''} ${u.last_name || ''}`.trim(), role: 'Athlete', phone: profile?.cell_phone || u.phone, email: u.email },
    { name: profile?.guardian1_name, role: 'Guardian', phone: profile?.guardian1_phone, email: profile?.guardian1_email },
    { name: profile?.guardian2_name, role: 'Guardian', phone: profile?.guardian2_phone, email: profile?.guardian2_email },
  ].filter(c => c.name || c.phone || c.email);

  return (
    <div className="cn-tabbody">
      <div className="cn-details-row">
        <ProfileCard userId={userId} onEditProfile={onEditProfile} />
        <div className="cn-tabbody">
          <section className="cn-card">
            <div className="cn-card__head"><h2 className="cn-card__title">Engagement</h2></div>
            {[['Last login', lastLoginAt], ['Profile updated', lastProfileChangeAt], ['Last managed', lastManagedAt]].map(([k, v]) => (
              <div key={k} className="cn-row cn-kv"><span>{k}</span><span>{v ? `${daysAgoText(v)} · ${fmtMonDay(v)}` : 'Never'}</span></div>
            ))}
          </section>
          <section className="cn-card">
            <div className="cn-card__head">
              <h2 className="cn-card__title">Camps</h2>
              {camps && <span className="cn-card__sub">{camps.registered.length} registered · {camps.attended.length} attended</span>}
            </div>
            {campsErr && <div className="cn-card__err">{campsErr}</div>}
            {campsErr ? null : camps === null ? <div className="cn-loading">Loading…</div> : (
              <>
                {camps.registered.length > 0 && <div className="cn-group cn-tone-green">Registered</div>}
                {camps.registered.map(c => <CampRow key={`r${c.camp_id}`} c={c} pill={c.payment_due
                  ? <span className="cn-pill cn-pill--amber">Payment due</span>
                  : <span className="cn-pill cn-pill--solid">Registered</span>} />)}
                {camps.attended.length > 0 && <div className="cn-group cn-tone-past">Attended</div>}
                {camps.attended.map(c => <CampRow key={`a${c.camp_id}`} c={c} pill={c.verified_count > 0
                  ? <span className="cn-pill cn-pill--green">{c.verified_count} verified</span>
                  : <span className="cn-pill cn-pill--neutral">Attended</span>} />)}
                {camps.registered.length + camps.attended.length === 0 && <div className="cn-empty-line">No camps yet.</div>}
              </>
            )}
          </section>
        </div>
      </div>
      <div className="cn-details-row">
        <div className="cn-tabbody">
          <section className="cn-card">
            <div className="cn-card__head"><h2 className="cn-card__title">Assignment</h2>
              <span className="cn-card__sub">Who owns this athlete, their contract term, and where they project</span></div>
            <div className="cn-assign2">
              <label className="cn-field2"><span className="cn-field2__label">Maximum recruiting level</span>
                <select className="cn-vinput" value={classification} onChange={onClassification}>
                  <option value="">— Not set —</option>
                  {CONCIERGE_CLASSIFICATIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
                <span className="cn-field2__help">The highest level we expect this athlete to reach.</span>
              </label>
              <div className="cn-field2"><span className="cn-field2__label">Owner</span>
                {me?.role === 'partner'
                  ? <div className="cn-vinput" data-testid="concierge-owner-readonly">{owner?.name || '—'}</div>
                  : (
                    <select className="cn-vinput" aria-label="Owner" value={owner?.user_id ?? ''} onChange={onOwner}>
                      <option value="">— Unassigned —</option>
                      {eligibleOwners.map(o => (
                        <option key={o.user_id} value={o.user_id}>
                          {o.email ? `${o.name} · ${o.email}` : o.name}{o.role === 'partner' ? ' (partner)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
              </div>
              <div className="cn-field2"><label className="cn-field2__label" htmlFor="cn-contract-start">Contract start</label>
                <input id="cn-contract-start" className="cn-vinput" type="date" value={contractStart ?? ''}
                  onChange={e => setContractStart(e.target.value)} onBlur={onContractBlur} /></div>
              <div className="cn-field2"><label className="cn-field2__label" htmlFor="cn-contract-end">Contract end</label>
                <input id="cn-contract-end" className={`cn-vinput${endingSoon ? ' cn-vinput--warn' : ''}`} type="date"
                  value={contractEnd ?? ''} onChange={e => setContractEnd(e.target.value)} onBlur={onContractBlur} />
                {endingSoon && <span className="cn-field2__warn">{endsIn === 0 ? 'Ends today' : `Ends in ${endsIn} day${endsIn === 1 ? '' : 's'}`}</span>}
              </div>
            </div>
          </section>
          <PaymentPanel userId={userId} />
        </div>
        <div className="cn-tabbody">
          <section className="cn-card">
            <div className="cn-card__head"><h2 className="cn-card__title">Contacts</h2></div>
            {contacts.length === 0 ? <div className="cn-empty-line">No contacts on file.</div> : contacts.map((c, i) => (
              <div key={i} className="cn-row cn-contact2">
                <div className="cn-contact2__name"><span>{c.name || '—'}</span> <span className="cn-contact2__role">{c.role}</span></div>
                <div className="cn-contact2__sub">{[c.phone && formatPhone(c.phone), c.email].filter(Boolean).join(' · ')}</div>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
}
